/**
 * Plain words for the audit log's action values.
 *
 * Every key is an action the backend actually writes (collected from every
 * log_admin_action call). The mockup's example events were illustrations, not
 * real names, and were not used. Anything not listed -- a new action added
 * later -- still reads sensibly: underscores become spaces.
 */
const LABELS = {
  login_success: 'Signed in',
  login_failed: 'Failed sign-in',
  login_rejected_inactive: 'Sign-in refused: account deactivated',
  reauth_failed: 'Password confirmation failed',
  refresh_token_reuse: 'Reused session token detected',

  twofa_enabled: 'Two-factor turned on',
  twofa_enrolled: 'Two-factor set up',
  twofa_disabled: 'Two-factor turned off',
  twofa_recovery_used: 'Two-factor recovery code used',
  twofa_verify_failed: 'Two-factor code refused',

  password_changed: 'Password changed',
  password_change_failed: 'Password change failed',
  password_reset_issued: 'Reset link issued',
  password_reset_revoked: 'Reset link revoked',
  password_reset_completed: 'Password reset completed',

  create_user: 'Member created',
  activate_user: 'Member reactivated',
  deactivate_user: 'Member deactivated',
  delete_user: 'Member deleted',
  registry_viewed: 'Registry opened',

  create_operator: 'Operator created',
  activate_operator: 'Operator reactivated',
  deactivate_operator: 'Operator deactivated',
  delete_operator: 'Operator deleted',
  assign_user_to_operator: 'Member assigned to operator',
  unassign_user_from_operator: 'Member unassigned from operator',

  onboarding_invite_created: 'Invite created',
  onboarding_invite_used: 'Invite accepted',
  onboarding_invite_revoked: 'Invite revoked',

  broadcast: 'Broadcast sent',
  case_closed: 'Case closed',
  case_reopened: 'Case reopened',

  capsule_created: 'Secure Capsule sent',
  capsule_opened: 'Secure Capsule opened',
  capsule_burned: 'Secure Capsule burned',

  media_uploaded: 'File uploaded',
  media_upload_rejected: 'File upload refused',
  media_deleted: 'File deleted',

  commons_message_removed: 'Commons post removed',
  commons_member_muted: 'Member silenced in the Commons',
  commons_member_unmuted: 'Member allowed to speak in the Commons',

  sworn_invited: 'Sworn invitation sent',
  sworn_invite_withdrawn: 'Sworn invitation withdrawn',
  sworn_accepted: 'Sworn invitation accepted',
  sworn_admitted: 'Admitted to the Sworn',
  sworn_removed: 'Removed from the Sworn',
  sworn_message_removed: 'Sworn message removed',

  vault_initialized: 'Vault created',
  vault_rekeyed: 'Vault passphrase changed',
  vault_reset: 'Vault reset',

  burn_armed: 'Last Resort armed',
  burn_disarmed: 'Last Resort disarmed',
  burn_arm_refused: 'Last Resort arming refused',
};

export function auditLabel(action) {
  if (!action) return 'Unknown action';
  if (LABELS[action]) return LABELS[action];
  const words = String(action).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Security-relevant events, marked so they stand out in a list. */
const ATTENTION = new Set([
  'login_failed', 'reauth_failed', 'refresh_token_reuse', 'twofa_verify_failed',
  'twofa_disabled', 'twofa_recovery_used', 'password_change_failed',
  'burn_armed', 'burn_arm_refused', 'vault_reset', 'registry_viewed',
]);

export function needsAttention(action) {
  return ATTENTION.has(action);
}

export const KNOWN_ACTIONS = Object.keys(LABELS);
